import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Loader } from 'components/Loader';
import { useSelectorJs } from 'utils/customHooks';
import { getOpParams } from 'utils/getOpParams';

import { sidebarItems } from './constants/sidebarItems';
import { visitorRoutes } from './constants/routes';
import { useFirstAvailableVisitorRoute } from './utils/customHooks/useFirstAvailableVisitorRoute';
import { ImmutableRootState } from './store/store';
import { IntegrationState } from './store/slices/integrationSlice';

/**
 * Redirects the user to the first visitor page they can access, based on their
 * scopes. Used as the fallback for scope-guarded routes (e.g. the dashboard)
 * so a user without that page's scope lands on an accessible page instead of a
 * 404 or an infinite loader. Mirrors invisit-web's `NavigateToFirstAvailablePage`.
 *
 * If the user has no scoped page at all, they fall back to the visitor mobile
 * page, which is always accessible.
 */
export const NavigateToFirstAvailablePage = () => {
  const navigate = useNavigate();
  const { orgId } = getOpParams();

  const { isMsiIntegrated, isGuestPassIntegrated, isTokenValid } =
    useSelectorJs((state: ImmutableRootState) =>
      state.get('integration'),
    ) as IntegrationState;

  const menuItems = sidebarItems({
    isMsiIntegrated,
    isGuestPassIntegrated,
    isTokenValid,
  });

  const { route, isLoading } = useFirstAvailableVisitorRoute(menuItems);

  useEffect(() => {
    if (isLoading) return;
    // Fall back to the always-accessible mobile page when there is no scoped
    // page the user can reach.
    const targetRoute = route ?? visitorRoutes.mobile;
    navigate(`/o/${orgId}/${targetRoute}`, { replace: true });
  }, [isLoading, route, orgId, navigate]);

  return <Loader content="Loading Visitor..." />;
};
